import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { quotationsAPI } from '../utils/api';
import { useConfirm } from '../hooks/useConfirm';
import RowActionsMenu from '../components/RowActionsMenu';
import { format } from 'date-fns';
import {
  FiPlus, FiSearch, FiFileText, FiSend, FiCopy,
  FiTrash2, FiDownload, FiEye, FiEdit2, FiCheck, FiX
} from 'react-icons/fi';
import './Quotations.css';

const Quotations = () => {
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  useEffect(() => {
    fetchQuotations();
  }, [pagination.page, status]);

  const fetchQuotations = async () => {
    try {
      setLoading(true);
      const response = await quotationsAPI.getAll({
        search,
        status,
        page: pagination.page,
        limit: pagination.limit,
      });
      setQuotations(response.data.quotations);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages,
      }));
    } catch (error) {
      toast.error('Failed to load quotations');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchQuotations();
  };

  const handleSend = async (quotation) => {
    const confirmed = await confirm({
      title: 'Mark as Sent',
      message: `Mark quotation ${quotation.quote_number} as sent?`,
      type: 'info',
      confirmText: 'Mark as Sent',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) {
      return;
    }

    try {
      await quotationsAPI.send(quotation.id);
      toast.success('Quotation marked as sent');
      fetchQuotations();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send quotation');
    }
  };

  const handleDuplicate = async (quotation) => {
    try {
      const response = await quotationsAPI.duplicate(quotation.id);
      toast.success('Quotation duplicated successfully');
      // Navigate to the new quotation
      window.location.href = `/quotations/${response.data.quotationId}`;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to duplicate quotation');
    }
  };

  const handleDelete = async (quotation) => {
    const confirmed = await confirm({
      title: 'Delete Quotation',
      message: `Are you sure you want to delete quotation ${quotation.quote_number}? This action cannot be undone.`,
      type: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) {
      return;
    }

    try {
      await quotationsAPI.delete(quotation.id);
      toast.success('Quotation deleted successfully');
      fetchQuotations();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete quotation');
    }
  };

  const handleDownloadPDF = async (quotation) => {
    try {
      const response = await quotationsAPI.getPDF(quotation.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${quotation.quote_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  const handleAccept = async (quotation) => {
    const confirmed = await confirm({
      title: 'Accept Quotation',
      message: `Mark quotation ${quotation.quote_number} as accepted?`,
      type: 'success',
      confirmText: 'Accept',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) {
      return;
    }

    try {
      await quotationsAPI.accept(quotation.id);
      toast.success('Quotation marked as accepted');
      fetchQuotations();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to accept quotation');
    }
  };

  const handleReject = async (quotation) => {
    const confirmed = await confirm({
      title: 'Reject Quotation',
      message: `Mark quotation ${quotation.quote_number} as rejected?`,
      type: 'warning',
      confirmText: 'Reject',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) {
      return;
    }

    try {
      await quotationsAPI.reject(quotation.id);
      toast.success('Quotation marked as rejected');
      fetchQuotations();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reject quotation');
    }
  };

  const handleConvert = async (quotation) => {
    const confirmed = await confirm({
      title: 'Convert to Invoice',
      message: `Convert quotation ${quotation.quote_number} to invoice?`,
      type: 'info',
      confirmText: 'Convert',
      cancelText: 'Cancel'
    });
    
    if (!confirmed) {
      return;
    }

    try {
      const response = await quotationsAPI.convert(quotation.id);
      toast.success('Quotation converted to invoice successfully');
      // Navigate to the new invoice
      window.location.href = `/invoices/${response.data.invoiceId}`;
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to convert quotation');
    }
  };

  const formatCurrency = (amount, currency = 'AED') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return format(new Date(date), 'MMM dd, yyyy');
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      draft: 'badge-draft',
      sent: 'badge-sent',
      viewed: 'badge-viewed',
      accepted: 'badge-accepted',
      rejected: 'badge-rejected',
      expired: 'badge-expired',
      converted: 'badge-converted',
      converted_to_project: 'badge-converted',
      change_requested: 'badge-viewed',
      change_request: 'badge-viewed',
    };
    return statusClasses[status] || 'badge-draft';
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      draft: 'Draft',
      sent: 'Sent',
      viewed: 'Viewed',
      accepted: 'Accepted',
      rejected: 'Rejected',
      expired: 'Expired',
      converted: 'Converted',
      converted_to_project: 'Converted',
      change_requested: 'Change Requested',
      change_request: 'Change Requested',
    };
    return statusLabels[status] || status;
  };

  const copyPublicLink = (quotation) => {
    const url = `${window.location.origin}/quotation/${quotation.public_token}`;
    navigator.clipboard.writeText(url);
    toast.success('Public link copied to clipboard');
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Quotations</h1>
          <p>Manage your quotations</p>
        </div>
        <Link to="/quotations/create" className="btn btn-primary">
          <FiPlus /> Create Quotation
        </Link>
      </div>

      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSearch} className="search-bar">
            <div className="search-input">
              <FiSearch className="search-input-icon" />
              <input
                type="text"
                className="form-control"
                placeholder="Search quotations..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              className="form-control"
              style={{ width: 'auto' }}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setSearchParams(e.target.value ? { status: e.target.value } : {});
              }}
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="viewed">Viewed</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
              <option value="expired">Expired</option>
              <option value="converted">Converted</option>
              <option value="converted_to_project">Converted</option>
            </select>
            <button type="submit" className="btn btn-secondary">
              Search
            </button>
          </form>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
            </div>
          ) : quotations.length === 0 ? (
            <div className="empty-state">
              <FiFileText className="empty-state-icon" />
              <h3 className="empty-state-title">No quotations found</h3>
              <p className="empty-state-text">
                {search || status ? 'Try adjusting your search criteria' : 'Get started by creating your first quotation'}
              </p>
              {!search && !status && (
                <Link to="/quotations/create" className="btn btn-primary">
                  <FiPlus /> Create Quotation
                </Link>
              )}
            </div>
          ) : (
            <>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Quotation</th>
                      <th>Client</th>
                      <th>Issue Date</th>
                      <th>Expiry Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotations.map((quotation) => (
                      <tr key={quotation.id}>
                        <td>
                          <Link to={`/quotations/${quotation.id}`} className="text-primary font-medium">
                            {quotation.quote_number}
                          </Link>
                        </td>
                        <td>
                          <div>
                            <div className="font-medium">{quotation.company_name}</div>
                            <div className="text-muted text-sm">{quotation.client_email}</div>
                          </div>
                        </td>
                        <td>{formatDate(quotation.issue_date)}</td>
                        <td>
                          <span className={new Date(quotation.expiry_date) < new Date() && !['accepted', 'converted', 'converted_to_project'].includes(quotation.status) ? 'text-danger' : ''}>
                            {formatDate(quotation.expiry_date)}
                          </span>
                        </td>
                        <td>
                          <div>
                            <div className="font-medium">{formatCurrency(quotation.total, quotation.currency)}</div>
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${getStatusBadge(quotation.status)}`}>
                            {getStatusLabel(quotation.status)}
                          </span>
                        </td>
                        <td>
                          <RowActionsMenu
                            actions={[
                              { label: 'View', icon: FiEye, className: 'btn-secondary', primary: true, onClick: () => navigate(`/quotations/${quotation.id}`) },
                              { label: 'Edit', icon: FiEdit2, className: 'btn-secondary', primary: quotation.status === 'draft', show: quotation.status === 'draft', onClick: () => navigate(`/quotations/${quotation.id}/edit`) },
                              { label: 'Send', icon: FiSend, className: 'btn-success', show: quotation.status === 'draft', onClick: () => handleSend(quotation) },
                              { label: 'Download PDF', icon: FiDownload, className: 'btn-secondary', onClick: () => handleDownloadPDF(quotation) },
                              { label: 'Copy Public Link', icon: FiCopy, className: 'btn-secondary', onClick: () => copyPublicLink(quotation) },
                              { label: 'Duplicate', icon: FiCopy, className: 'btn-secondary', onClick: () => handleDuplicate(quotation) },
                              { label: 'Accept', icon: FiCheck, className: 'btn-success', show: ['sent', 'viewed'].includes(quotation.status), onClick: () => handleAccept(quotation) },
                              { label: 'Reject', icon: FiX, className: 'btn-warning', show: ['sent', 'viewed'].includes(quotation.status), onClick: () => handleReject(quotation) },
                              { label: 'Convert to Invoice', icon: FiFileText, className: 'btn-primary', show: quotation.status === 'accepted', onClick: () => handleConvert(quotation) },
                              { label: 'Delete', icon: FiTrash2, className: 'btn-danger', danger: true, primary: !['converted', 'converted_to_project', 'accepted'].includes(quotation.status), show: !['converted', 'converted_to_project', 'accepted'].includes(quotation.status), onClick: () => handleDelete(quotation) },
                            ]}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pagination.totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    disabled={pagination.page === 1}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  >
                    Previous
                  </button>
                  <span className="pagination-info">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    className="pagination-btn"
                    disabled={pagination.page === pagination.totalPages}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Quotations;